import React, { useMemo, useRef, useState } from "react";
import AuthLayout from "./AuthLayout";
import "./Auth.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authApi } from "../../api/client";
import { getApiErrorMessage } from "../../api/http";
import { saveResetToken } from "../../utils/session";

const VerifyCode = () => {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const email = useMemo(
    () => location.state?.email || sessionStorage.getItem("ieee_reset_email") || "",
    [location.state]
  );
  const phone = useMemo(
    () => location.state?.phone || sessionStorage.getItem("ieee_reset_phone") || "",
    [location.state]
  );

  const token = digits.join("");

  const handleDigitChange = (index, value) => {
    const normalized = `${value || ""}`.replace(/\D/g, "");
    if (!normalized) {
      const cleared = [...digits];
      cleared[index] = "";
      setDigits(cleared);
      return;
    }

    if (normalized.length > 1) {
      const next = [...digits];
      normalized.slice(0, 6 - index).split("").forEach((char, offset) => {
        next[index + offset] = char;
      });
      setDigits(next);
      const nextFocusIndex = Math.min(index + normalized.length, 5);
      inputRefs.current[nextFocusIndex]?.focus();
      return;
    }

    const next = [...digits];
    next[index] = normalized;
    setDigits(next);

    if (index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pastedDigits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pastedDigits) return;

    event.preventDefault();
    const next = ["", "", "", "", "", ""];
    pastedDigits.split("").forEach((char, index) => {
      next[index] = char;
    });
    setDigits(next);
    inputRefs.current[Math.min(pastedDigits.length, 6) - 1]?.focus();
  };

  const handleVerify = async () => {
    setError("");
    if (token.length !== 6) {
      setError("Please enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      await authApi.verifyPasswordResetToken({ token });
      saveResetToken(token);
      navigate("/reset-password");
    } catch (err) {
      setError(getApiErrorMessage(err, "Invalid or expired verification code"));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email || !phone) {
      setError("Please go back and enter your email and phone again.");
      return;
    }
    setError("");
    setMessage("");
    try {
      await authApi.requestPasswordReset({ email, phone });
      setMessage("Code resent successfully.");
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not resend code"));
    }
  };

  return (
    <AuthLayout title="Check Your Email" subtitle="We have sent a code to you !">
      <h2 className="fw-bold text-center mb-4">Verification Code</h2>
      <div className="d-flex gap-2 justify-content-center mb-4">
        {digits.map((digit, i) => (
          <input
            key={i}
            className="form-control text-center custom-input"
            style={{ width: "45px", height: "55px" }}
            maxLength="1"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            value={digit}
            onChange={(e) => handleDigitChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
          />
        ))}
      </div>
      <div className="text-center mb-4">
        <button type="button" onClick={handleResend} className="btn btn-link p-0 small text-decoration-none resendCode">
          Resend code
        </button>
      </div>
      {error ? <div className="alert alert-danger py-2 small">{error}</div> : null}
      {message ? <div className="alert alert-success py-2 small">{message}</div> : null}
      <button disabled={loading} onClick={handleVerify} className="btn btn-primary w-100 py-2 mb-3 fw-bold mt-3 btn-signin">
        {loading ? "Verifying..." : "Verify Code"}
      </button>
      <p className="text-center small">Remember the password? <Link to="/" className="fw-bold text-decoration-none ">Go Back</Link></p>
    </AuthLayout>
  );
};

export default VerifyCode;
